import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {createSelector} from '@ngrx/store';
import {PatronAccountStatus} from '@store/patron/patron.types';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {RouteName} from '@app/app.route-names.generated';
import {isPopulatedArray} from '@aviary/store/common/typing.helpers';
import {MatIcon} from '@angular/material/icon';
import {NgTemplateOutlet} from '@angular/common';
import {MxSelector} from '@aviary/store/store.types';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {ButtonRouteNameDirective} from '@lib/directives/button-route-name/button-route-name.directive';
import {LinkButtonComponent} from '@aviary/buttons/link-button/link-button.component';

export interface AccountWarning {
  message: string;
  actionLabel?: string;
  actionRoute?: RouteName;
}

@Component({
  selector: 'px-account-warning-header',
  host: {
    class: 'flex flex-row justify-center w-full bg-alert'
  },
  templateUrl: './account-warning-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, NgTemplateOutlet, SelectPipe, ButtonRouteNameDirective, LinkButtonComponent]
})
export class AccountWarningHeaderComponent {
  private readonly formatPipe = inject(FormatPipe);

  private generateWarnings(status: PatronAccountStatus): AccountWarning[] {
    // If the patron has blocks on their account...
    if (isPopulatedArray(status.blocks)) {
      // Create specific warnings for each block, if applicable
      const blockWarnings: AccountWarning[] = [];
      for (const block of status.blocks) {
        const warning: AccountWarning | undefined = this.createWarningFromBlock(block, status);
        if (warning) {
          blockWarnings.push(warning);
        }
      }

      // If we generated specific warnings, return them.
      if (isPopulatedArray(blockWarnings)) return blockWarnings;

      // There are other blocks on the account, return a generic warning instead.
      return [{
        message: `Your account currently isn't able to check out or renew items. Please contact the library for help.`
      }];
    }

    return [];
  }

  private createWarningFromBlock(block: string, status: PatronAccountStatus): AccountWarning | undefined {
    switch (block) {
      case 'MAX_FEE_LIMIT':
        return {
          message: `Your account balance exceeds ${this.formatMaxFee(status.maximumAccountBalance)}. Make a payment to restore your account.`,
          actionLabel: 'Make a Payment',
          actionRoute: 'BALANCE'
        };
      case 'MAX_ITEMS_OUT':
        return {
          message: `You've reached your checkout limit. Once you return an item, you'll be able to check out more.`,
          actionLabel: 'View Items Out',
          actionRoute: 'CHECKED_OUT'
        };
      case 'MAX_LOST':
        return {
          message: 'Your account is currently suspended because you have too many lost or damaged items. Contact the library for assistance.'
        };
      case 'CARD_EXPIRED':
        return {
          message: 'Your library card has expired. Please contact the library to renew it.'
        };
      default:
        return undefined;
    }
  }

  private formatMaxFee(amount: number): string {
    return this.formatPipe.transform(amount, 'CURRENCY');
  }

  public readonly accountWarnings: MxSelector<AccountWarning[]> = createSelector(
    PatronSelectors.AccountStatus.status,
    status => this.generateWarnings(status)
  );
}
